import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import Layout from '../../components/layout/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Banknote, 
  CreditCard, 
  FileDown, 
  Heart,
  Building,
  Copy,
  Check
} from 'lucide-react';
import { toast } from 'sonner';

const DonationsPage = () => {
  const { t, lang } = useLanguage();
  const [copiedField, setCopiedField] = React.useState(null);

  const bankDetails = {
    ron: {
      iban: 'RO49 AAAA 1B31 0075 9384 0000',
      bank: 'Banca Transilvania',
      beneficiary: 'Asociatia "Societatea Umanitara SEMILUNA"',
    },
    eur: {
      iban: 'RO49 BBBB 1B31 0075 9384 0001',
      bank: 'Banca Transilvania',
      beneficiary: 'Asociatia "Societatea Umanitara SEMILUNA"',
      swift: 'BTRLRO22',
    },
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <Layout>
      {/* Header */}
      <section className="bg-islamic-teal text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-serif text-3xl md:text-4xl font-bold mb-2">
            {t('donations.title')}
          </h1>
          <p className="text-white/70">{t('donations.subtitle')}</p>
        </div>
      </section>

      <section className="py-8 md:py-12 bg-islamic-pearl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Bank Details */}
            <div className="space-y-6">
              <h2 className="font-serif text-2xl font-semibold text-stone-800 flex items-center gap-3">
                <Building className="w-6 h-6 text-islamic-teal" />
                {t('donations.bankDetails')}
              </h2>

              {/* RON Account */}
              <Card className="border-stone-200 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-islamic-teal/10 flex items-center justify-center">
                      <span className="font-semibold text-islamic-teal">RON</span>
                    </div>
                    <h3 className="font-semibold text-lg text-stone-800">{t('donations.ronAccount')}</h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                      <div>
                        <p className="text-xs text-stone-500 uppercase">{t('donations.iban')}</p>
                        <p className="font-mono text-sm text-stone-800">{bankDetails.ron.iban}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyToClipboard(bankDetails.ron.iban, 'ron-iban')}
                        data-testid="copy-ron-iban"
                      >
                        {copiedField === 'ron-iban' ? (
                          <Check className="w-4 h-4 text-islamic-teal" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs text-stone-500 uppercase">{t('donations.bank')}</p>
                      <p className="text-sm text-stone-800">{bankDetails.ron.bank}</p>
                    </div>
                    
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs text-stone-500 uppercase">{t('donations.beneficiary')}</p>
                      <p className="text-sm text-stone-800">{bankDetails.ron.beneficiary}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* EUR Account */}
              <Card className="border-stone-200 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-islamic-gold/10 flex items-center justify-center">
                      <span className="font-semibold text-islamic-gold">EUR</span>
                    </div>
                    <h3 className="font-semibold text-lg text-stone-800">{t('donations.eurAccount')}</h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                      <div>
                        <p className="text-xs text-stone-500 uppercase">{t('donations.iban')}</p>
                        <p className="font-mono text-sm text-stone-800">{bankDetails.eur.iban}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyToClipboard(bankDetails.eur.iban, 'eur-iban')}
                        data-testid="copy-eur-iban"
                      >
                        {copiedField === 'eur-iban' ? (
                          <Check className="w-4 h-4 text-islamic-teal" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                      <div>
                        <p className="text-xs text-stone-500 uppercase">SWIFT/BIC</p>
                        <p className="font-mono text-sm text-stone-800">{bankDetails.eur.swift}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyToClipboard(bankDetails.eur.swift, 'swift')}
                        data-testid="copy-swift"
                      >
                        {copiedField === 'swift' ? (
                          <Check className="w-4 h-4 text-islamic-teal" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs text-stone-500 uppercase">{t('donations.bank')}</p>
                      <p className="text-sm text-stone-800">{bankDetails.eur.bank}</p>
                    </div>
                    
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs text-stone-500 uppercase">{t('donations.beneficiary')}</p>
                      <p className="text-sm text-stone-800">{bankDetails.eur.beneficiary}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tax Redirect & Campaigns */}
            <div className="space-y-6">
              {/* 2% Tax Redirect */}
              <Card className="border-islamic-gold border-2 shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-lg bg-islamic-gold/10 flex items-center justify-center">
                      <span className="font-bold text-islamic-gold text-lg">2%</span>
                    </div>
                    <h3 className="font-serif text-xl font-semibold text-stone-800">
                      {t('donations.taxRedirect')}
                    </h3>
                  </div>
                  
                  <p className="text-stone-600 mb-6">
                    {t('donations.taxDescription')}
                  </p>
                  
                  <Button 
                    className="w-full bg-islamic-gold hover:bg-islamic-gold/90 text-islamic-teal font-medium"
                    data-testid="download-tax-form"
                  >
                    <FileDown className="w-4 h-4 mr-2" />
                    {t('donations.downloadForm')}
                  </Button>
                </CardContent>
              </Card>

              {/* How to help */}
              <Card className="border-stone-200 shadow-md bg-gradient-to-br from-islamic-teal to-islamic-teal-light text-white">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Heart className="w-8 h-8" />
                    <h3 className="font-serif text-xl font-semibold">
                      {lang === 'ar' ? 'كيف تساعدنا' : lang === 'en' ? 'How to Help' : 'Cum să ajuți'}
                    </h3>
                  </div>
                  
                  <ul className="space-y-3 text-white/90">
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm shrink-0">1</span>
                      <span>{lang === 'ar' ? 'تبرع مباشر عبر التحويل البنكي' : lang === 'en' ? 'Direct bank transfer donation' : 'Donație directă prin transfer bancar'}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm shrink-0">2</span>
                      <span>{lang === 'ar' ? 'إعادة توجيه 2% من ضريبة الدخل' : lang === 'en' ? 'Redirect 2% of your income tax' : 'Redirecționează 2% din impozit'}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm shrink-0">3</span>
                      <span>{lang === 'ar' ? 'التطوع في أنشطة المسجد' : lang === 'en' ? 'Volunteer at mosque activities' : 'Voluntariat la activitățile moscheii'}</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Gratitude */}
              <div className="text-center py-6">
                <p className="font-arabic text-2xl text-islamic-teal mb-2">جزاكم الله خيرا</p>
                <p className="text-stone-600">
                  {lang === 'ar' ? 'شكراً لدعمكم' : lang === 'en' ? 'Thank you for your support' : 'Vă mulțumim pentru sprijin'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default DonationsPage;
